import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Tooltip,
  CircularProgress,
  TextField,
  InputAdornment,
  Pagination,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  styled,
  useTheme,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormLabel,
  Collapse,
  Grid,
  Divider,
  Card,
  CardContent,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
  Refresh as RefreshIcon,
  ArrowDropDown,
  Close as CloseIcon,
  FilterList as FilterListIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Visibility as VisibilityIcon,
} from '@mui/icons-material';
import useFetchObjects from '../../api/useFetchObjects';
import useFetchObject from '../../api/useFetchObject';
import useDelete from '../../api/useDelete';
import useAdd from '../../api/useAdd';
import useUpdate from '../../api/useUpdate';
import { useAuth } from '../../context/AuthContext';

// RTL-aware TextField component
const RTLTextField = styled(TextField)(({ theme, isRtl }) => ({
  ...(isRtl && {
    '& .MuiInputBase-input': {
      textAlign: 'right',
      direction: 'rtl',
      padding: '10px 14px 16px',
    },
    '& .MuiInputLabel-root': {
      right: 25,
      left: 'auto',
      transformOrigin: 'right',
      marginRight: '5px',
    },
    '& .MuiOutlinedInput-root': {
      paddingLeft: 14,
      paddingRight: 0,
    },
    '& .MuiInputAdornment-root': {
      marginRight: 8,
      marginLeft: 0,
    },
    '& .MuiOutlinedInput-notchedOutline': {
      textAlign: 'right',
    },
    '& .MuiFormHelperText-root': {
      textAlign: 'right',
    },
  }),
}));

// RTL-aware TableCell component with bold header option
const RTLTableCell = styled(TableCell)(({ theme, isRtl, align, isHeader }) => ({
  ...(isRtl && {
    textAlign: align || 'right',
    direction: 'rtl',
  }),
  ...(isHeader && {
    fontWeight: 'bold',
  }),
}));

// RTL-aware FormControl
const RTLFormControl = styled(FormControl)(({ theme, isRtl }) => ({
  ...(isRtl && {
    '& .MuiInputLabel-root': {
      right: 25,
      left: 'auto',
      transformOrigin: 'right',
      marginRight: '5px',
    },
    '& .MuiInputBase-input': {
      textAlign: 'right',
      direction: 'rtl',
    },
    '& .MuiSelect-select': {
      paddingRight: '32px',
      textAlign: 'right',
      paddingLeft: '32px',
    },
    '& .MuiSelect-icon': {
      right: 'auto',
      left: 7,
      pointerEvents: 'none',
      color: theme.palette.action.active,
    },
  }),
}));

// Custom styled button for better RTL support
const RTLButton = styled(Button)(({ theme, isRtl }) => ({
  ...(isRtl && {
    flexDirection: 'row-reverse',
  }),
}));

const RTLIconButton = styled(IconButton)(({ theme, isRtl }) => ({
  ...(isRtl && {
    transform: 'scaleX(-1)',
  }),
}));

// RTL Dialog Title
const RTLDialogTitle = styled(DialogTitle)(({ theme, isRtl }) => ({
  ...(isRtl && {
    textAlign: 'right',
    direction: 'rtl',
  }),
}));

// RTL Dialog Content
const RTLDialogContent = styled(DialogContent)(({ theme, isRtl }) => ({
  ...(isRtl && {
    textAlign: 'right',
    direction: 'rtl',
  }),
}));

// RTL Dialog Actions
const RTLDialogActions = styled(DialogActions)(({ theme, isRtl }) => ({
  ...(isRtl && {
    flexDirection: 'row-reverse',
  }),
}));

const DirectorateList = () => {
  const { t, i18n } = useTranslation(['directorates', 'common', 'validation']);
  const { user } = useAuth();
  const token = user?.token;
  const isRtl = i18n.language === 'dari' || i18n.language === 'ps';
  const theme = useTheme();
  
  // State for filtering and pagination
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [search, setSearch] = useState('');
  const [deputyMinistryFilter, setDeputyMinistryFilter] = useState('');
  const [showFilters, setShowFilters] = useState(false);

  // State for dialog management
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [selectedDirectorateId, setSelectedDirectorateId] = useState(null);

  // Form state
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    deputy_ministry_id: '',
    ministry_id: '',
    parent_type: 'ministry' // Default parent type (ministry or deputy)
  });

  // Validation state
  const [errors, setErrors] = useState({});

  // Query parameters for API call
  const params = {
    page,
    limit,
    search,
    deputyMinistryId: deputyMinistryFilter || undefined
  };
  
  // Fetch directorates
  const { 
    data, 
    isLoading, 
    isError, 
    error,
    refetch 
  } = useFetchObjects('directorates', params, token);
  
  // Fetch deputy ministries for filter dropdown
  const { 
    data: deputyMinistriesData, 
    isLoading: isLoadingDeputyMinistries 
  } = useFetchObjects('deputy-ministries', {}, token);
  
  const deputyMinistries = Array.isArray(deputyMinistriesData) ? deputyMinistriesData : (deputyMinistriesData?.data || []);

  // Fetch ministries to display hierarchy information
  const { 
    data: ministriesData, 
    isLoading: isLoadingMinistries 
  } = useFetchObjects('ministries', {}, token);
  
  const ministries = Array.isArray(ministriesData) ? ministriesData : (ministriesData?.data || []);

  // Fetch directorate data for edit
  const { 
    data: directorateData, 
    isLoading: isLoadingDirectorate,
  } = useFetchObject('directorates', selectedDirectorateId ? `directorates/${selectedDirectorateId}` : null, selectedDirectorateId, token);

  // Delete functionality
  const { handleDelete, ConfirmDialog } = useDelete('directorates', token);

  // Create functionality
  const { 
    handleAdd, 
    loading: isCreating, 
    success: createSuccess 
  } = useAdd(
    'directorates',
    token,
    '/directorates',
    t('directorates:create_success'),
    t('directorates:create_error')
  );

  // Update functionality
  const { 
    handleUpdate, 
    loading: isUpdating, 
    success: updateSuccess 
  } = useUpdate(
    'directorates',
    token,
    '/directorates',
    t('directorates:update_success'),
    t('directorates:update_error')
  );

  // Handle search input change
  const handleSearchChange = (e) => {
    setSearch(e.target.value);
    setPage(1); // Reset to first page when search changes
  };

  // Handle deputy ministry filter change
  const handleDeputyMinistryChange = (e) => {
    setDeputyMinistryFilter(e.target.value);
    setPage(1); // Reset to first page when filter changes
  };

  // Handle page change
  const handlePageChange = (event, value) => {
    setPage(value);
  };

  // Handle page size change
  const handleLimitChange = (e) => {
    setLimit(Number(e.target.value));
    setPage(1); // Reset to first page when limit changes
  };

  // Open create dialog
  const handleOpenCreateDialog = () => {
    setFormData({
      name: '',
      description: '',
      deputy_ministry_id: '',
      ministry_id: '',
      parent_type: 'ministry' // Default parent type (ministry or deputy)
    });
    setErrors({});
    setCreateDialogOpen(true);
  };

  // Close create dialog
  const handleCloseCreateDialog = () => {
    setCreateDialogOpen(false);
  };

  // Open edit dialog
  const handleOpenEditDialog = (id) => {
    setSelectedDirectorateId(id);
    setEditDialogOpen(true);
  };

  // Close edit dialog
  const handleCloseEditDialog = () => {
    setEditDialogOpen(false);
    setSelectedDirectorateId(null);
  };

  // Handle input change
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    
    // Clear error for this field
    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: null,
      }));
    }
    
    // If changing parent type, clear errors on the parent fields
    if (name === 'parent_type') {
      setErrors((prev) => ({
        ...prev,
        deputy_ministry_id: null,
        ministry_id: null,
      }));
    }
  };

  // Set form data when directorate data is loaded (for edit)
  React.useEffect(() => {
    if (directorateData && editDialogOpen) {
      const directorate = directorateData.data || directorateData;
      
      setFormData({
        name: directorate.name || '',
        description: directorate.description || '',
        deputy_ministry_id: directorate.deputy_ministry_id || '',
        ministry_id: directorate.ministry_id || '',
        parent_type: directorate.parent_type || 'ministry'
      });
    }
  }, [directorateData, editDialogOpen]);

  // Reset form and close dialog when operation is successful
  React.useEffect(() => {
    if (createSuccess) {
      handleCloseCreateDialog();
      refetch();
    }
    
    if (updateSuccess) {
      handleCloseEditDialog();
      refetch();
    }
  }, [createSuccess, updateSuccess]);

  // Validate form
  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.name.trim()) {
      newErrors.name = t('validation:required');
    }
    
    if (formData.parent_type === 'deputy') {
      if (!formData.deputy_ministry_id) {
        newErrors.deputy_ministry_id = t('validation:required');
      }
    } else if (formData.parent_type === 'ministry') {
      if (!formData.ministry_id) {
        newErrors.ministry_id = t('validation:required');
      }
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle create form submission
  const handleCreateSubmit = (e) => {
    e.preventDefault();
    
    // Validate form
    if (!validateForm()) {
      return;
    }
    
    // Create a copy of the form data
    const submissionData = { ...formData };
    
    // Handle parent type selection - clear the unused parent ID
    if (submissionData.parent_type === 'ministry') {
      submissionData.deputy_ministry_id = null;
      if (submissionData.ministry_id === '') {
        submissionData.ministry_id = null;
      }
    } else if (submissionData.parent_type === 'deputy') {
      submissionData.ministry_id = null;
    if (submissionData.deputy_ministry_id === '') {
      submissionData.deputy_ministry_id = null;
      }
    }
    
    // Submit form data
    handleAdd(submissionData);
  };

  // Handle edit form submission
  const handleEditSubmit = (e) => {
    e.preventDefault();
    
    // Validate form
    if (!validateForm()) {
      return;
    }
    
    // Create a copy of the form data
    const submissionData = { ...formData };
    
    // Handle parent type selection - clear the unused parent ID
    if (submissionData.parent_type === 'ministry') {
      submissionData.deputy_ministry_id = null;
      if (submissionData.ministry_id === '') {
        submissionData.ministry_id = null;
      }
    } else if (submissionData.parent_type === 'deputy') {
      submissionData.ministry_id = null;
    if (submissionData.deputy_ministry_id === '') {
      submissionData.deputy_ministry_id = null;
      }
    }
    
    // Submit form data
    handleUpdate(selectedDirectorateId, submissionData);
  };

  // Calculate total pages
  const totalPages = data?.totalPages || data?.pagination?.totalPages || 1;
  
  // Directorates list
  const directorates = data?.directorates || data?.data || (Array.isArray(data) ? data : []);

  const totalItems = data?.count ?? data?.total ?? data?.pagination?.total ?? directorates.length;

  return (
    <Box dir={isRtl ? 'rtl' : 'ltr'} sx={{ width: '100%' }}>
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        mb: 3,
        flexDirection: isRtl ? 'row-reverse' : 'row',
        width: '100%'
      }}>
        <RTLButton
          variant="contained"
          startIcon={!isRtl && <AddIcon />}
          endIcon={isRtl && <AddIcon />}
          onClick={handleOpenCreateDialog}
          isRtl={isRtl}
          sx={{ 
            whiteSpace: 'nowrap',
            borderRadius: 2,
            px: 2,
            py: 0.75,
            textTransform: 'none',
            fontWeight: 500,
            boxShadow: 2,
            '&:hover': {
              boxShadow: 4
            }
          }}
        >
          {t('create_new')}
        </RTLButton>
        
        <Typography 
          variant="h4" 
          component="h1" 
          sx={{ 
            textAlign: isRtl ? 'right' : 'left',
            fontWeight: 'bold',
          }}
        >
          {t('list_title')}
        </Typography>
      </Box>
      
      {/* Search and filters */}
      <Box sx={{ 
        display: 'flex', 
        mb: 3, 
        gap: 2,
        flexDirection: isRtl ? 'row-reverse' : 'row',
        width: '100%'
      }}>
        <RTLTextField
          variant="outlined"
          size="small"
          placeholder={t('common:search')}
          value={search}
          onChange={handleSearchChange}
          sx={{ 
            flexGrow: 1,
            '& .MuiOutlinedInput-root': {
              paddingRight: isRtl ? '0px' : '0',
              paddingLeft: isRtl ? '0' : '0px',
            },
            '& .MuiOutlinedInput-input': {
              padding: '10px 14px',
            }
          }}
          isRtl={isRtl}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
            dir: isRtl ? 'rtl' : 'ltr',
          }}
        />
        
        <RTLButton 
          variant="contained" 
          onClick={() => {
            setPage(1);
            refetch();
          }}
          isRtl={isRtl}
        >
          {t('common:search')}
        </RTLButton>
        
        <RTLButton 
          variant="outlined" 
          onClick={() => {
            setSearch('');
            setDeputyMinistryFilter('');
            refetch();
          }}
          isRtl={isRtl}
        >
          {t('common:reset')}
        </RTLButton>
        
        <RTLButton 
          variant="outlined" 
          startIcon={!isRtl && <RefreshIcon />}
          endIcon={isRtl && <RefreshIcon />}
          onClick={refetch}
          isRtl={isRtl}
        >
          {t('common:refresh')}
        </RTLButton>
        
        <RTLButton
          variant="text"
          color="primary"
          onClick={() => setShowFilters(!showFilters)}
          startIcon={!isRtl && (showFilters ? <ExpandLessIcon /> : <ExpandMoreIcon />)}
          endIcon={isRtl && (showFilters ? <ExpandLessIcon /> : <ExpandMoreIcon />)}
          isRtl={isRtl}
        >
          <FilterListIcon fontSize="small" sx={{ mr: 0.5 }} />
          {t('common:filter')}
        </RTLButton>
      </Box>

      <Collapse in={showFilters}>
        <Paper sx={{ p: 2, mb: 3, borderRadius: 2 }}>
          <Box sx={{ 
            display: 'flex', 
            flexDirection: isRtl ? 'row-reverse' : 'row', 
            gap: 2, 
            flexWrap: 'wrap' 
          }}>
            <RTLFormControl 
              sx={{ minWidth: 200, flexGrow: 1 }} 
              size="small"
              isRtl={isRtl}
            >
              <InputLabel id="deputy-ministry-filter-label">{t('filter_by_deputy_ministry')}</InputLabel>
              <Select
                labelId="deputy-ministry-filter-label"
                value={deputyMinistryFilter}
                onChange={handleDeputyMinistryChange}
                label={t('filter_by_deputy_ministry')}
                MenuProps={{
            anchorOrigin: {
              vertical: 'bottom',
              horizontal: isRtl ? 'right' : 'left',
            },
            transformOrigin: {
              vertical: 'top',
              horizontal: isRtl ? 'right' : 'left',
            },
            PaperProps: {
              style: {
                direction: isRtl ? 'rtl' : 'ltr',
              },
            },
                }}
                IconComponent={(props) => (
              <span
                {...props}
                style={{
                  position: 'absolute',
                  right: isRtl ? 'auto' : '7px',
                  left: isRtl ? '7px' : 'auto',
                  pointerEvents: 'none',
                }}
              >
                <ArrowDropDown />
              </span>
                )}
        >
          <MenuItem value="">
            <em>{t('common:all')}</em>
          </MenuItem>
          {isLoadingDeputyMinistries ? (
            <MenuItem disabled>
              <CircularProgress size={20} /> {t('common:loading')}
            </MenuItem>
          ) : (
            deputyMinistries.map((ministry) => (
              <MenuItem key={ministry.id} value={ministry.id}>
                {ministry.name}
                      {ministry.ministry?.name && (
                        <Typography variant="caption" sx={{ ml: 1, color: 'text.secondary' }}>
                          ({ministry.ministry.name})
                        </Typography>
                      )}
              </MenuItem>
            ))
          )}
              </Select>
            </RTLFormControl>
      </Box>
        </Paper>
      </Collapse>

      {/* Error alert */}
      {isError && (
        <Alert severity="error" sx={{ mb: 3, textAlign: isRtl ? 'right' : 'left' }}>
          {error?.message || t('error_occurred')}
        </Alert>
      )}

      {/* Table */}
      <Paper sx={{ width: '100%', overflow: 'hidden', mb: 3 }}>
        <TableContainer>
          <Table>
            <TableHead sx={{ 
              bgcolor: theme.palette.grey[100],
              '& .MuiTableRow-root': {
                borderBottom: `2px solid ${theme.palette.grey[300]}`,
              }
            }}>
              <TableRow>
                <RTLTableCell 
                  isRtl={isRtl} 
                  isHeader
                  align={isRtl ? "right" : "left"}
                  sx={{ 
                    fontSize: '1rem',
                    fontWeight: 600,
                    color: theme.palette.text.primary,
                    textAlign: isRtl ? 'right' : 'left'
                  }}
                >
                   {t('id')}
                </RTLTableCell>
                <RTLTableCell 
                  isRtl={isRtl} 
                  isHeader
                  sx={{ 
                    fontSize: '1rem',
                    fontWeight: 600,
                    color: theme.palette.text.primary
                  }}
                >
                  {t('name')}
                </RTLTableCell>
                <RTLTableCell 
                  isRtl={isRtl} 
                  isHeader
                  sx={{ 
                    fontSize: '1rem',
                    fontWeight: 600,
                    color: theme.palette.text.primary
                  }}
                >
                  {t('description')}
                </RTLTableCell>
                <RTLTableCell 
                  isRtl={isRtl} 
                  isHeader
                  sx={{ 
                    fontSize: '1rem',
                    fontWeight: 600,
                    color: theme.palette.text.primary
                  }}
                >
                  {t('parent_organization')}
                </RTLTableCell>
                <RTLTableCell 
                  isRtl={isRtl} 
                  isHeader
                  sx={{ 
                    fontSize: '1rem',
                    fontWeight: 600,
                    color: theme.palette.text.primary
                  }}
                >
                  {t('created_at')}
                </RTLTableCell>
                <RTLTableCell 
                  isRtl={isRtl} 
                  align={isRtl ? "right" : "right"} 
                  isHeader
                  sx={{ 
                    fontSize: '1rem',
                    fontWeight: 600,
                    color: theme.palette.text.primary,
                    textAlign: isRtl ? 'right' : 'right',
                  }}
                >
                  {t('common:actions')}
                </RTLTableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {isLoading ? (
                <TableRow>
                  <RTLTableCell colSpan={6} align="center" sx={{ py: 3 }} isRtl={isRtl}>
                    <CircularProgress size={30} />
                  </RTLTableCell>
                </TableRow>
              ) : directorates.length === 0 ? (
                <TableRow>
                  <RTLTableCell colSpan={6} align="center" sx={{ py: 3 }} isRtl={isRtl}>
                    {t('common:no_records')}
                  </RTLTableCell>
                </TableRow>
              ) : (
                directorates.map((directorate, index) => (
                  <TableRow 
                    key={directorate.id} 
                    hover
                    sx={{ 
                      '&:hover': { backgroundColor: 'grey.50' },
                      '& td': { 
                        py: 1.5, // Increased vertical padding for better spacing
                      } 
                    }}
                  >
                    <RTLTableCell isRtl={isRtl} align={isRtl ? "right" : "center"}>
                      <Typography variant="body2" sx={{ 
                        fontWeight: 500,
                        textAlign: isRtl ? 'right' : 'center' 
                      }}>
                        {(page - 1) * limit + index + 1}
                      </Typography>
                    </RTLTableCell>
                    <RTLTableCell isRtl={isRtl} sx={{ fontWeight: 500 }}>
                      {directorate.name}
                    </RTLTableCell>
                    <RTLTableCell isRtl={isRtl}>
                      {directorate.description || '-'}
                    </RTLTableCell>
                    <RTLTableCell isRtl={isRtl}>
                      {directorate.ministry ? (
                        <Box>
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            {directorate.ministry.name}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {t('direct_under_ministry')}
                          </Typography>
                        </Box>
                      ) : directorate.deputyMinistry ? (
                        <Box>
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            {directorate.deputyMinistry.name}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {directorate.deputyMinistry.ministry?.name ? `${t('ministry')}: ${directorate.deputyMinistry.ministry.name}` : ''}
                          </Typography>
                        </Box>
                      ) : t('not_assigned')}
                    </RTLTableCell>
                    <RTLTableCell isRtl={isRtl}>
                      {new Date(directorate.created_at).toLocaleDateString()}
                    </RTLTableCell>
                    <RTLTableCell isRtl={isRtl} align={isRtl ? "left" : "right"}>
                      <Box sx={{ display: 'flex', justifyContent: isRtl ? 'flex-start' : 'flex-end', gap: 1 }}>
                        <Tooltip title={t('common:edit')} placement={isRtl ? "right" : "left"}>
                          <IconButton
                            size="small"
                            onClick={() => handleOpenEditDialog(directorate.id)}
                            color="primary"
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title={t('common:delete')} placement={isRtl ? "right" : "left"}>
                          <IconButton 
                            size="small"
                            onClick={() => handleDelete(directorate.id)}
                            color="error"
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </RTLTableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        
        {/* Pagination */}
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          p: 2,
          flexDirection: isRtl ? 'row-reverse' : 'row' 
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Typography 
              variant="body2" 
              color="text.secondary"
              sx={{ 
                mr: isRtl ? 0 : 2, 
                ml: isRtl ? 2 : 0,
                whiteSpace: 'nowrap'
              }}
            >
              {t('common:rows_per_page')}:
            </Typography>
            <Select
              value={limit}
              onChange={handleLimitChange}
              size="small"
              sx={{ 
                minWidth: 70,
                '& .MuiSelect-select': {
                  px: 2
                },
                ...(isRtl && {
                  '& .MuiSelect-select': {
                    paddingRight: '32px',
                    textAlign: 'right',
                    paddingLeft: '14px',
                  },
                  '& .MuiSelect-icon': {
                    right: 7,
                    left: 'auto',
                  },
                }),
              }}
              MenuProps={{
                disableScrollLock: true,
                anchorOrigin: {
                  vertical: 'top',
                  horizontal: isRtl ? 'right' : 'left',
                },
                transformOrigin: {
                  vertical: 'top',
                  horizontal: isRtl ? 'right' : 'left',
                },
              }}
            >
              <MenuItem value={5}>5</MenuItem>
              <MenuItem value={10}>10</MenuItem>
              <MenuItem value={25}>25</MenuItem>
              <MenuItem value={50}>50</MenuItem>
            </Select>
          </Box>
          
          {/* Make sure we always show pagination even if total is just 1 page */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
              {t('common:showing')} {(page - 1) * limit + 1}-
              {Math.min(page * limit, totalItems)} {t('common:of')} {totalItems}
            </Typography>
            <Pagination
              count={Math.max(totalPages, 1)}  
              page={page}
              onChange={handlePageChange}
              color="primary"
              dir={isRtl ? 'rtl' : 'ltr'}
              shape="rounded"
              size="small"
              showFirstButton
              showLastButton
            />
          </Box>
        </Box>
      </Paper>
      
      {/* Create Dialog */}
      <Dialog 
        open={createDialogOpen} 
        onClose={handleCloseCreateDialog}
        fullWidth
        maxWidth="sm"
        dir={isRtl ? 'rtl' : 'ltr'}
        PaperProps={{
          sx: {
            borderRadius: '8px',
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.08)',
            maxWidth: '600px',
            width: '100%',
            mx: 'auto',
          }
        }}
        sx={{
          '& .MuiDialog-container': {
            justifyContent: 'center',
            alignItems: 'center',
            '& .MuiPaper-root': {
              margin: '0 auto',
              position: 'absolute',
              top: '50%',
              left: '42%',
              transform: 'translate(-50%, -50%)',
            },
          },
          '& .MuiBackdrop-root': {
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
          }
        }}
      >
        <RTLDialogTitle isRtl={isRtl} sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          fontWeight: 'bold',
          pb: 1,
          pt: 2,
          height: '60px',
          borderBottom: '1px solid',
          borderColor: 'divider',
        }}>
          <Typography variant="h5">{t('create_title')}</Typography>
          <IconButton onClick={handleCloseCreateDialog} size="small">
            <CloseIcon />
          </IconButton>
        </RTLDialogTitle>
        
        <RTLDialogContent isRtl={isRtl} sx={{ pt: 3 }}>
          <Box component="form" id="create-form" onSubmit={handleCreateSubmit}>
            <RTLTextField
              fullWidth
              name="name"
              label={t('name')}
              value={formData.name}
              onChange={handleInputChange}
              error={!!errors.name}
              helperText={errors.name}
              required
              margin="normal"
              isRtl={isRtl}
              InputProps={{
                dir: isRtl ? 'rtl' : 'ltr',
              }}
              FormHelperTextProps={{
                style: { textAlign: isRtl ? 'right' : 'left' }
              }}
            />
            
            <RTLTextField
              fullWidth
              name="description"
              label={t('description')}
              value={formData.description || ''}
              onChange={handleInputChange}
              multiline
              rows={3}
              margin="normal"
              isRtl={isRtl}
              InputProps={{
                dir: isRtl ? 'rtl' : 'ltr',
              }}
            />
            
            {/* Parent Type Selection */}
            <Box sx={{ 
              my: 2, 
              width: '100%',
              textAlign: isRtl ? 'right' : 'left',
            }}>
              <FormControl component="fieldset" sx={{ width: '100%' }}>
                <FormLabel component="legend" sx={{ 
                  textAlign: isRtl ? 'right' : 'left',
                  width: '100%',
                  marginBottom: 1,
                }}>
                  {t('parent_type')}
                </FormLabel>
                <RadioGroup
                  row
                  name="parent_type"
                  value={formData.parent_type}
                  onChange={handleInputChange}
                  sx={{ 
                    flexDirection: 'row',
                    justifyContent: isRtl ? 'flex-start' : 'flex-start',
                    width: '100%'
                  }}
                >
                  <FormControlLabel
                    value="ministry"
                    control={<Radio />}
                    label={t('ministry')}
                    sx={{ 
                      marginRight: isRtl ? 0 : 2,
                      marginLeft: isRtl ? 2 : 0,
                    }}
                  />
                  <FormControlLabel
                    value="deputy"
                    control={<Radio />}
                    label={t('deputy_ministry')}
                  />
                </RadioGroup>
              </FormControl>
            </Box>
            
            {/* Ministry Parent Selection - shown only when parent_type is ministry */}
            {formData.parent_type === 'ministry' && (
            <RTLTextField
              select
              fullWidth
                name="ministry_id"
                label={t('ministry')}
                value={formData.ministry_id || ''}
                onChange={handleInputChange}
                margin="normal"
                isRtl={isRtl}
                InputProps={{
                  dir: isRtl ? 'rtl' : 'ltr',
                }}
                error={!!errors.ministry_id}
                helperText={errors.ministry_id}
                required
                SelectProps={{
                  disableScrollLock: true,
                  anchorOrigin: {
                    vertical: 'bottom',
                    horizontal: isRtl ? 'right' : 'left',
                  },
                  transformOrigin: {
                    vertical: 'top',
                    horizontal: isRtl ? 'right' : 'left',
                  },
                  PaperProps: {
                    style: {
                      direction: isRtl ? 'rtl' : 'ltr',
                    },
                  },
                  IconComponent: (props) => (
                    <span
                      {...props}
                      style={{
                        position: 'absolute',
                        right: isRtl ? 'auto' : '7px',
                        left: isRtl ? '7px' : 'auto',
                        pointerEvents: 'none',
                      }}
                    >
                      <ArrowDropDown />
                    </span>
                  ),
                  sx: {
                    ...(isRtl && {
                      paddingRight: '14px',
                      paddingLeft: '14px',
                      '& .MuiSelect-select': {
                        paddingRight: '14px',
                        paddingLeft: '32px',
                      },
                    }),
                  },
                }}
              >
                <MenuItem value="">
                  <em>{t('none')}</em>
                </MenuItem>
                {isLoadingMinistries ? (
                  <MenuItem disabled>
                    <CircularProgress size={20} /> {t('common:loading')}
                  </MenuItem>
                ) : (
                  ministries.map((ministry) => (
                    <MenuItem key={ministry.id} value={ministry.id}>
                      {ministry.name}
                    </MenuItem>
                  ))
                )}
              </RTLTextField>
            )}
            
            {/* Deputy Ministry Parent Selection - shown only when parent_type is deputy */}
            {formData.parent_type === 'deputy' && (
              <RTLTextField
                select
                fullWidth
                labelId="deputy-ministry-label"
              name="deputy_ministry_id"
              label={t('deputy_ministry')}
              value={formData.deputy_ministry_id || ''}
              onChange={handleInputChange}
              margin="normal"
              isRtl={isRtl}
              InputProps={{
                dir: isRtl ? 'rtl' : 'ltr',
              }}
                error={!!errors.deputy_ministry_id}
                helperText={errors.deputy_ministry_id}
                required
              SelectProps={{
                disableScrollLock: true,
                anchorOrigin: {
                  vertical: 'bottom',
                  horizontal: isRtl ? 'right' : 'left',
                },
                transformOrigin: {
                  vertical: 'top',
                  horizontal: isRtl ? 'right' : 'left',
                },
                PaperProps: {
                  style: {
                    direction: isRtl ? 'rtl' : 'ltr',
                  },
                },
                IconComponent: (props) => (
                  <span
                    {...props}
                    style={{
                      position: 'absolute',
                      right: isRtl ? 'auto' : '7px',
                      left: isRtl ? '7px' : 'auto',
                      pointerEvents: 'none',
                    }}
                  >
                    <ArrowDropDown />
                  </span>
                ),
                  sx: {
                    ...(isRtl && {
                      paddingRight: '14px',
                      paddingLeft: '14px',
                      '& .MuiSelect-select': {
                        paddingRight: '14px',
                        paddingLeft: '32px',
                      },
                    }),
                  },
              }}
            >
              <MenuItem value="">
                <em>{t('none')}</em>
              </MenuItem>
              {isLoadingDeputyMinistries ? (
                <MenuItem disabled>
                  <CircularProgress size={20} /> {t('common:loading')}
                </MenuItem>
              ) : (
                deputyMinistries.map((ministry) => (
                  <MenuItem key={ministry.id} value={ministry.id}>
                    {ministry.name}
                      {ministry.ministry?.name && (
                        <Typography variant="caption" sx={{ ml: 1, color: 'text.secondary' }}>
                          ({ministry.ministry.name})
                        </Typography>
                      )}
                  </MenuItem>
                ))
              )}
            </RTLTextField>
            )}
          </Box>
        </RTLDialogContent>
        
        <RTLDialogActions isRtl={isRtl} sx={{ 
          px: 3, 
          pb: 3, 
          gap: 1,
          borderTop: '1px solid',
          borderColor: 'divider',
          pt: 2
        }}>
          <RTLButton 
            variant="outlined"
            onClick={handleCloseCreateDialog}
            isRtl={isRtl}
          >
            {t('common:cancel')}
          </RTLButton>
          <RTLButton 
            variant="contained"
            type="submit"
            form="create-form"
            disabled={isCreating}
            isRtl={isRtl}
          >
            {isCreating ? (
              <CircularProgress size={24} />
            ) : (
              t('common:save')
            )}
          </RTLButton>
        </RTLDialogActions>
      </Dialog>
      
      {/* Edit Dialog */}
      <Dialog 
        open={editDialogOpen} 
        onClose={handleCloseEditDialog}
        fullWidth
        maxWidth="sm"
        dir={isRtl ? 'rtl' : 'ltr'}
        PaperProps={{
          sx: {
            borderRadius: '8px',
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.08)',
            maxWidth: '600px',
            width: '100%',
            mx: 'auto',
          }
        }}
        sx={{
          '& .MuiDialog-container': {
            justifyContent: 'center',
            alignItems: 'center',
            '& .MuiPaper-root': {
              margin: '0 auto',
              position: 'absolute',
              top: '50%',
              left: '42%',
              transform: 'translate(-50%, -50%)',
            },
          },
          '& .MuiBackdrop-root': {
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
          }
        }}
      >
        <RTLDialogTitle isRtl={isRtl} sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          fontWeight: 'bold',
          pb: 1,
          pt: 2,
          height: '60px',
          borderBottom: '1px solid',
          borderColor: 'divider',
        }}>
          <Typography variant="h5">{t('edit_title')}</Typography>
          <IconButton onClick={handleCloseEditDialog} size="small">
            <CloseIcon />
          </IconButton>
        </RTLDialogTitle>
        
        <RTLDialogContent isRtl={isRtl} sx={{ pt: 3 }}>
          {isLoadingDirectorate ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', my: 3 }}>
              <CircularProgress />
            </Box>
          ) : (
            <Box component="form" id="edit-form" onSubmit={handleEditSubmit}>
              <RTLTextField
                fullWidth
                name="name"
                label={t('name')}
                value={formData.name}
                onChange={handleInputChange}
                error={!!errors.name}
                helperText={errors.name}
                required
                margin="normal"
                isRtl={isRtl}
                InputProps={{
                  dir: isRtl ? 'rtl' : 'ltr',
                }}
                FormHelperTextProps={{
                  style: { textAlign: isRtl ? 'right' : 'left' }
                }}
              />
              
              <RTLTextField
                fullWidth
                name="description"
                label={t('description')}
                value={formData.description || ''}
                onChange={handleInputChange}
                multiline
                rows={3}
                margin="normal"
                isRtl={isRtl}
                InputProps={{
                  dir: isRtl ? 'rtl' : 'ltr',
                }}
              />
              
              {/* Parent Type Selection */}
              <Box sx={{ 
                my: 2, 
                width: '100%',
                textAlign: isRtl ? 'right' : 'left',
              }}>
                <FormControl component="fieldset" sx={{ width: '100%' }}>
                  <FormLabel component="legend" sx={{ 
                    textAlign: isRtl ? 'right' : 'left',
                    width: '100%',
                    marginBottom: 1,
                  }}>
                    {t('parent_type')}
                  </FormLabel>
                  <RadioGroup
                    row
                    name="parent_type"
                    value={formData.parent_type}
                    onChange={handleInputChange}
                    sx={{ 
                      flexDirection: 'row',
                      justifyContent: isRtl ? 'flex-start' : 'flex-start',
                      width: '100%'
                    }}
                  >
                    <FormControlLabel
                      value="ministry"
                      control={<Radio />}
                      label={t('ministry')}
                      sx={{ 
                        marginRight: isRtl ? 0 : 2,
                        marginLeft: isRtl ? 2 : 0,
                      }}
                    />
                    <FormControlLabel
                      value="deputy"
                      control={<Radio />}
                      label={t('deputy_ministry')}
                    />
                  </RadioGroup>
                </FormControl>
              </Box>
              
              {/* Ministry Parent Selection - shown only when parent_type is ministry */}
              {formData.parent_type === 'ministry' && (
              <RTLTextField
                select
                fullWidth
                  name="ministry_id"
                  label={t('ministry')}
                  value={formData.ministry_id || ''}
                  onChange={handleInputChange}
                  margin="normal"
                  isRtl={isRtl}
                  InputProps={{
                    dir: isRtl ? 'rtl' : 'ltr',
                  }}
                  error={!!errors.ministry_id}
                  helperText={errors.ministry_id}
                  required
                  SelectProps={{
                    disableScrollLock: true,
                    anchorOrigin: {
                      vertical: 'bottom',
                      horizontal: isRtl ? 'right' : 'left',
                    },
                    transformOrigin: {
                      vertical: 'top',
                      horizontal: isRtl ? 'right' : 'left',
                    },
                    PaperProps: {
                      style: {
                        direction: isRtl ? 'rtl' : 'ltr',
                      },
                    },
                    IconComponent: (props) => (
                      <span
                        {...props}
                        style={{
                          position: 'absolute',
                          right: isRtl ? 'auto' : '7px',
                          left: isRtl ? '7px' : 'auto',
                          pointerEvents: 'none',
                        }}
                      >
                        <ArrowDropDown />
                      </span>
                    ),
                    sx: {
                      ...(isRtl && {
                        paddingRight: '14px',
                        paddingLeft: '14px',
                        '& .MuiSelect-select': {
                          paddingRight: '14px',
                          paddingLeft: '32px',
                        },
                      }),
                    },
                  }}
                >
                  <MenuItem value="">
                    <em>{t('none')}</em>
                  </MenuItem>
                  {isLoadingMinistries ? (
                    <MenuItem disabled>
                      <CircularProgress size={20} /> {t('common:loading')}
                    </MenuItem>
                  ) : (
                    ministries.map((ministry) => (
                      <MenuItem key={ministry.id} value={ministry.id}>
                        {ministry.name}
                      </MenuItem>
                    ))
                  )}
                </RTLTextField>
              )}
              
              {/* Deputy Ministry Parent Selection - shown only when parent_type is deputy */}
              {formData.parent_type === 'deputy' && (
                <RTLTextField
                  select
                  fullWidth
                  labelId="deputy-ministry-label"
                name="deputy_ministry_id"
                label={t('deputy_ministry')}
                value={formData.deputy_ministry_id || ''}
                onChange={handleInputChange}
                margin="normal"
                isRtl={isRtl}
                InputProps={{
                  dir: isRtl ? 'rtl' : 'ltr',
                }}
                  error={!!errors.deputy_ministry_id}
                  helperText={errors.deputy_ministry_id}
                  required
                SelectProps={{
                  disableScrollLock: true,
                  anchorOrigin: {
                    vertical: 'bottom',
                    horizontal: isRtl ? 'right' : 'left',
                  },
                  transformOrigin: {
                    vertical: 'top',
                    horizontal: isRtl ? 'right' : 'left',
                  },
                  PaperProps: {
                    style: {
                      direction: isRtl ? 'rtl' : 'ltr',
                    },
                  },
                  IconComponent: (props) => (
                    <span
                      {...props}
                      style={{
                        position: 'absolute',
                        right: isRtl ? 'auto' : '7px',
                        left: isRtl ? '7px' : 'auto',
                        pointerEvents: 'none',
                      }}
                    >
                      <ArrowDropDown />
                    </span>
                  ),
                    sx: {
                      ...(isRtl && {
                        paddingRight: '14px',
                        paddingLeft: '14px',
                        '& .MuiSelect-select': {
                          paddingRight: '14px',
                          paddingLeft: '32px',
                        },
                      }),
                    },
                }}
              >
                <MenuItem value="">
                  <em>{t('none')}</em>
                </MenuItem>
                {isLoadingDeputyMinistries ? (
                  <MenuItem disabled>
                    <CircularProgress size={20} /> {t('common:loading')}
                  </MenuItem>
                ) : (
                  deputyMinistries.map((ministry) => (
                    <MenuItem key={ministry.id} value={ministry.id}>
                      {ministry.name}
                        {ministry.ministry?.name && (
                          <Typography variant="caption" sx={{ ml: 1, color: 'text.secondary' }}>
                            ({ministry.ministry.name})
                          </Typography>
                        )}
                    </MenuItem>
                  ))
                )}
              </RTLTextField>
              )}
            </Box>
          )}
        </RTLDialogContent>
        
        <RTLDialogActions isRtl={isRtl} sx={{ 
          px: 3, 
          pb: 3, 
          gap: 1,
          borderTop: '1px solid',
          borderColor: 'divider',
          pt: 2
        }}>
          <RTLButton 
            variant="outlined"
            onClick={handleCloseEditDialog}
            isRtl={isRtl}
          >
            {t('common:cancel')}
          </RTLButton>
          <RTLButton 
            variant="contained"
            type="submit"
            form="edit-form"
            disabled={isUpdating || isLoadingDirectorate}
            isRtl={isRtl}
          >
            {isUpdating ? (
              <CircularProgress size={24} />
            ) : (
              t('common:save')
            )}
          </RTLButton>
        </RTLDialogActions>
      </Dialog>
      
      {/* Delete confirmation dialog */}
      <ConfirmDialog message={t('delete_confirm_text')} />
    </Box>
  );
};

export default DirectorateList; 